import api from './client';

export interface ProxyStats {
  total: number;
  successes: number;
  failures: number;
  avg_duration_ms: number;
  by_operation: {
    operation: string;
    total: number;
    successes: number;
    failures: number;
    success_rate: number;
    avg_duration_ms: number;
  }[];
}

export interface ProxyError {
  timestamp: string;
  operation: string;
  error: string;
}

export interface ProxyLogEntry {
  ts: string;
  level: string;
  step: string;
  message: string;
}

export interface ProxyOperation {
  timestamp: string;
  operation: string;
  username: string;
  operator_email: string;
  success: boolean;
  error: string;
  duration_ms: number;
  // Persisted ops (from /admin/proxies/{org_id}/operations) also include these.
  // Live in-memory ops on org.servicebox.proxy_recent_operations don't.
  id?: string;
  ordre_id?: string;
  dossier_id?: string;
  request_data?: Record<string, unknown>;
}

export interface ProxyOperationsPage {
  items: ProxyOperation[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface ProxyOperationsFilters {
  ordre_id?: string;
  dossier_id?: string;
  operation?: string;
  success?: boolean;
  limit?: number;
  cursor_timestamp?: string;
}

export interface ProxySystemInfo {
  hostname: string;
  platform?: string;
  uptime_seconds?: number;
}

export interface ProxyOutage {
  start_at: string;
  end_at: string | null; // null while the outage is still ongoing
  duration_min: number;
  ongoing?: boolean;
}

// One physical machine that has registered as a proxy for this org.
export interface ProxyInstance {
  hostname: string | null;
  proxy_url: string;
  version: string;
  last_seen: string;
  blocked: boolean;
}

// Set when 2+ non-blocked machines report different URLs — they're fighting.
export interface ProxyConflict {
  detected_at: string;
  instances: { hostname: string | null; proxy_url: string }[];
}

export interface ProxyInfo {
  org_id: string;
  org_name: string;
  proxy_url: string | null;
  proxy_version: string;
  proxy_last_seen: string | null;
  proxy_last_outage_start_at?: string | null;
  proxy_last_outage_end_at?: string | null;
  proxy_last_outage_duration_min?: number | null;
  proxy_outages_30d?: ProxyOutage[];
  proxy_stats: ProxyStats | null;
  proxy_recent_errors: ProxyError[];
  proxy_recent_logs: ProxyLogEntry[];
  proxy_recent_operations: ProxyOperation[];
  proxy_system_info: ProxySystemInfo | null;
  proxy_instances?: ProxyInstance[];
  proxy_blocked_hostnames?: string[];
  proxy_conflict?: ProxyConflict | null;
  servicebox_enabled: boolean;
  servicebox_username: string;
  pending_commands: { action: string; queued_at: string }[];
  /** Last run of the proxy's canary scenario on the LIVE proxy (create a RDV a year out, read, delete, verify). */
  last_selftest?: SelftestReport | null;
  /** Runs addressed to one machine — the version canary proving a pre-release. Keyed by hostname. */
  last_selftest_by_host?: Record<string, SelftestReport>;
  /** When the daily Cloud Scheduler run was last queued. */
  selftest_last_queued_at?: string | null;
  /** Latest Alpha relay measurement (proxy >= 3.41.0); absent on orgs whose proxy never probed. */
  relay_last_probe?: RelayProbe | null;
}

/** One measurement of the Alpha relay from the garage machine (TCP connect, sometimes a timed GET). */
export interface RelayProbe {
  at: string;
  ok: boolean;
  ms: number | null;
  error: string;
  addresses?: { ip: string; ok: boolean; ms: number; error: string }[];
  http?: { ok: boolean; ms: number | null; status?: number; error?: string } | null;
}

export interface RelayAvailability {
  probes: number;
  reachable_pct: number | null;
  connect_ms_median: number | null;
  http_probes: number;
  http_ok_pct: number | null;
  http_ms_median: number | null;
  by_hour: { hour: number; probes: number; reachable_pct: number | null }[];
  by_day: { day: string; probes: number; reachable_pct: number | null }[];
  by_address: { ip: string; probes: number; reachable_pct: number | null }[];
  failure_streaks: { start_at: string; end_at: string; probes: number; error: string }[];
  last: RelayProbe | null;
}

export interface SelftestReport {
  executed_at?: string;
  success: boolean;
  ok: number;
  total: number;
  failed: string[];
  leftovers: string[];
  warnings: string[];
  error?: string;
  duration_ms?: number;
  /** true = the daily scheduled run, false = launched by hand */
  scheduled?: boolean;
  /** machine that ran it, and the proxy build it was running */
  hostname?: string;
  version?: string;
  steps: { name: string; ok: boolean; detail: string }[];
}

/** Real 30-day history, aggregated server-side from the persisted `proxy_operations`
 * collection. `org.servicebox.proxy_recent_operations` only holds the last 20 ops. */
export interface ProxyDailyOperations {
  days: string[];               // ISO dates (Europe/Paris), oldest first
  truncated: boolean;
  series: {
    org_id: string;
    counts: number[];           // one entry per day, same length/order as `days`
    failures: number[];
    total: number;
  }[];
}

export interface LatestVersion {
  version: string | null;
  tag?: string;
  published_at?: string;
}


/** One row of the waiting-time aggregation: a day, an operation or a user. */
export interface TimingStats {
  count: number;
  total_ms: number;
  avg_ms: number;
  p95_ms: number;
  max_ms: number;
}

export interface TimingDay extends TimingStats {
  day: string;
  failures: number;
}

export interface TimingOperation extends TimingStats {
  operation: string;
}

export interface TimingUser extends TimingStats {
  operator_email: string;
}

export interface SlowOperation {
  timestamp: string;
  operation: string;
  duration_ms: number;
  operator_email: string;
  org_id: string;
  ordre_id: string;
  dossier_id: string;
  success: boolean;
  /** "proxy" (ServiceBox/Alpha via the garage's proxy) or "backend" (direct ServiceBox call). */
  source: string;
  /** Per-phase breakdown in ms, when the operation measured its own steps. */
  steps: Record<string, number>;
  error: string;
}

/** Whose time is counted: what people waited for, what ran on its own, or both. */
export type TimingScope = "human" | "auto" | "all";

export interface OperationTimings {
  days: string[];
  scope: TimingScope;
  truncated: boolean;
  daily: TimingDay[];
  by_operation: TimingOperation[];
  by_user: TimingUser[];
  slowest: SlowOperation[];
  /** Same rows, newest first: "what just happened" rather than "what hurts". */
  recent: SlowOperation[];
  overall: TimingStats;
  /** Operations nobody waited for (autotest, synchros) — reported whatever the scope,
   *  because they hold the proxy lock and delay whoever is behind them. */
  automatic: TimingStats & { by_operation: TimingOperation[] };
}

export const proxyAdminApi = {
  list: async (): Promise<ProxyInfo[]> => {
    const { data } = await api.get('/admin/proxies');
    return data;
  },

  sendCommand: async (orgId: string, action: string) => {
    const { data } = await api.post(`/admin/proxies/${orgId}/command`, { action });
    return data;
  },

  blockHost: async (orgId: string, hostname: string, blocked: boolean) => {
    const { data } = await api.post(`/admin/proxies/${orgId}/block-host`, { hostname, blocked });
    return data;
  },

  /** Availability of the garage's Alpha relay, as measured by its proxy every 5 min. */
  getRelayAvailability: async (orgId: string, days = 7): Promise<RelayAvailability> => {
    const { data } = await api.get(`/admin/proxies/${orgId}/relay`, { params: { days } });
    return data;
  },

  /** Per-day operation counts per org over the last `days`, for the dashboard chart. */
  getOperationsDaily: async (days = 30): Promise<ProxyDailyOperations> => {
    const { data } = await api.get('/admin/proxies/operations-daily', { params: { days } });
    return data;
  },

  /** Where the waiting goes on ServiceBox and Alpha: per day, per operation, per user. */
  getTimings: async (days = 30, scope: TimingScope = "human", orgId?: string): Promise<OperationTimings> => {
    const { data } = await api.get('/admin/proxies/timings', {
      params: { days, scope, ...(orgId ? { org_id: orgId } : {}) },
    });
    return data;
  },

  getLatestVersion: async (): Promise<LatestVersion> => {
    const { data } = await api.get('/admin/proxies/latest-version');
    return data;
  },

  listOperations: async (orgId: string, filters: ProxyOperationsFilters = {}): Promise<ProxyOperationsPage> => {
    const params: Record<string, string | number | boolean> = {};
    if (filters.ordre_id) params.ordre_id = filters.ordre_id;
    if (filters.dossier_id) params.dossier_id = filters.dossier_id;
    if (filters.operation) params.operation = filters.operation;
    if (filters.success !== undefined) params.success = filters.success;
    if (filters.limit) params.limit = filters.limit;
    if (filters.cursor_timestamp) params.cursor_timestamp = filters.cursor_timestamp;
    const { data } = await api.get(`/admin/proxies/${orgId}/operations`, { params });
    return data;
  },

  /** Robot Alpha: state from the proxy heartbeat (proxy >= 3.64.0) + its commands still queued. */
  getAlphaRobotStatus: async (orgId: string): Promise<AlphaRobotStatus> => {
    const { data } = await api.get(`/admin/proxies/${orgId}/alpha-robot`);
    return data;
  },

  /** Robot Alpha results, newest first, without their pictures. */
  listAlphaRobotCommands: async (orgId: string, limit = 50, offset = 0): Promise<AlphaRobotCommandsPage> => {
    const { data } = await api.get(`/admin/proxies/${orgId}/alpha-robot/commands`, { params: { limit, offset } });
    return data;
  },

  /** Ask the robot for a picture of its whole session (also turns mode travaux on for 5 min). */
  requestAlphaRobotScreen: async (orgId: string): Promise<{ cmd_id: string; already_pending: boolean }> => {
    const { data } = await api.post(`/admin/proxies/${orgId}/alpha-robot/screen`);
    return data;
  },

  /** Latest picture of the robot's screen + the request still waiting, if any. */
  getAlphaRobotScreen: async (orgId: string): Promise<AlphaRobotScreen> => {
    const { data } = await api.get(`/admin/proxies/${orgId}/alpha-robot/screen`);
    return data;
  },

  /** Every picture the robot took, newest first (metadata only). */
  listAlphaRobotCaptures: async (orgId: string, limit = 24, offset = 0): Promise<AlphaRobotCapturesPage> => {
    const { data } = await api.get(`/admin/proxies/${orgId}/alpha-robot/captures`, { params: { limit, offset } });
    return data;
  },

  /** One picture's bytes, as an object URL (the endpoint needs the auth header, so no plain <img src>). */
  getAlphaRobotImageUrl: async (orgId: string, cmdId: string, index: number): Promise<string> => {
    const { data } = await api.get(`/admin/proxies/${orgId}/alpha-robot/commands/${cmdId}/images/${index}`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(data as Blob);
  },

  /** One robot result with its parsed response and the list of its pictures. */
  getAlphaRobotCommand: async (orgId: string, cmdId: string): Promise<AlphaRobotCommandDetail> => {
    const { data } = await api.get(`/admin/proxies/${orgId}/alpha-robot/commands/${cmdId}`);
    return data;
  },
};

export interface AlphaRobotState {
  found?: boolean;
  alive?: boolean;
  alpha_open?: boolean;
  version?: string;
  user?: string;
  allow_writes?: boolean;
  heartbeat_at?: string;
  started_at?: string;
  reported_at?: string;
  down_since?: string | null;
  // proxy >= 3.64.1
  alpha_login?: string | null;        // "ok <iso>" | "echec : ..." | "bloque : ..."
  alpha_crash?: { at?: string; reason?: string; capture_path?: string } | null;
  alpha_duplicates?: { at?: string; with_window?: number; others?: number; count?: number } | null;
  sessions?: { id?: number | string; station?: string; state?: string; user?: string }[];
  alpha_processes?: number | null;          // AlphaDMS processes in the robot's session
  alpha_processes_server?: number | null;   // on the whole server
}

export interface AlphaRobotScreen {
  capture: (AlphaRobotImage & { cmd_id: string; executed_at: string | null }) | null;
  last_attempt: { cmd_id: string; executed_at: string | null; ok: boolean; error: string } | null;
  pending_id: string | null;
}

export interface AlphaRobotStatus {
  org_id: string;
  org_name: string;
  reported: boolean;
  robot: AlphaRobotState | null;
  up: boolean | null;
  stale: boolean | null;
  proxy_version: string;
  proxy_last_seen: string | null;
  fast_heartbeat_until: string | null;
  pending: { id: string; action: string; op: string; queued_at: string | null; attempts: number }[];
}

export interface AlphaRobotCommand {
  id: string;
  action: string;
  op: string;
  executed_at: string | null;
  success: boolean;
  ok: boolean;
  error: string;
  summary: string;
  image_count: number;
  elapsed_ms: number | null;
  robot: { version?: string; user?: string; machine?: string; allow_writes?: boolean } | null;
  hostname: string;
  proxy_version: string;
}

export interface AlphaRobotCommandsPage {
  items: AlphaRobotCommand[];
  total: number | null;   // null until the whole history has been read
  has_more: boolean;
}

export interface AlphaRobotImage {
  index: number;
  mime: string;
  where: string;
  window: string;
  size_bytes: number;
}

export interface AlphaRobotCapture extends AlphaRobotImage {
  cmd_id: string;
  op: string;
  executed_at: string | null;
  ok: boolean;
  summary: string;
}

export interface AlphaRobotCapturesPage {
  items: AlphaRobotCapture[];
  has_more: boolean;
}

export interface AlphaRobotCommandDetail extends AlphaRobotCommand {
  payload: Record<string, unknown>;
  response: Record<string, unknown>;
  images: AlphaRobotImage[];
}
